/**
 * Submit Results Tool
 *
 * A special tool used by agents-as-tools (sub-agents) to submit their final
 * results back to the parent agent. This enables the "Submit Results Pattern":
 *
 * 1. Sub-agent receives requirements from parent (e.g., Orchestrator)
 * 2. Sub-agent uses its internal tools (e.g., searchAlphaSense)
 * 3. Sub-agent can loop multiple times, refining its search
 * 4. When satisfied, sub-agent calls submitResults with structured results
 * 5. Parent agent receives the results and continues its own loop
 *
 * This pattern is critical because:
 * - It allows the LLM to decide when it has gathered enough information
 * - It enables multiple search iterations before committing
 * - It provides structured, typed results to the parent agent
 * - It keeps the parent agent's tool loop clean
 */
import { ToolDescription, safeParseToolParams } from "./toolDescription";
import { ToolBase } from "./toolBase";
import { SubmitResultsParamsSchema, } from "./submitResults.schema";
// ═══════════════════════════════════════════════════════════════════════════
// SPECIAL MARKER FOR RESULT DETECTION
// ═══════════════════════════════════════════════════════════════════════════
/**
 * Special marker added to submitResults response so the tool loop can
 * detect that results have been submitted and exit accordingly.
 */
export const SUBMIT_RESULTS_MARKER = '__AGENT_RESULTS_SUBMITTED__';
// ═══════════════════════════════════════════════════════════════════════════
// SYSTEM PROMPT CONFIGURATION
// ═══════════════════════════════════════════════════════════════════════════
const SUBMIT_RESULTS_SYSTEM_PROMPT = `### submitResults
Submit your final results back to the orchestrator.

**When to use:**
- You have gathered sufficient information for ALL requirements
- You've determined some requirements cannot be fulfilled

**Call your other tools first** to gather information, then call this once ready.

\`\`\`json
{
  "results": [
    {
      "requirementId": "A",
      "relevantResults": { "searchResults": ["as_abc12", "as_def34"] },
      "rationale": "Found Q4 2024 revenue data in earnings call (as_abc12) and analyst report (as_def34)"
    }
  ],
  "summary": "Found financial data for all 3 requirements"
}
\`\`\`

**IMPORTANT:** Only call this when you're done - it terminates the agent loop.`;
// ═══════════════════════════════════════════════════════════════════════════
// SUBMIT RESULTS TOOL
// ═══════════════════════════════════════════════════════════════════════════
export class SubmitResultsTool extends ToolBase {
    constructor() {
        super();
        this.name = "submitResults";
        // System prompt description
        this.systemPromptDescription = SUBMIT_RESULTS_SYSTEM_PROMPT;
        this.description = new ToolDescription("submitResults", `Submit your final results back to the orchestrator.

Call this tool when you have gathered sufficient information to address all requirements.
Each requirement should have a corresponding result item with:
- requirementId: The ID from the original requirement ("A", "B", etc.)
- relevantResults: The data you found (format depends on your agent type)
- rationale: Why these results address the requirement

You can call your search tools multiple times before submitting. Only call submitResults
when you are confident you have addressed all requirements or determined that some
requirements cannot be fulfilled with available data.

Example for a search agent:
{
  "results": [
    {
      "requirementId": "A",
      "relevantResults": {
        "searchResults": ["as_abc12", "as_def34"]
      },
      "rationale": "Found Q4 2024 revenue data in earnings call transcript (as_abc12) and analyst report (as_def34)"
    }
  ],
  "summary": "Found relevant financial data for all requirements"
}`, SubmitResultsParamsSchema);
    }
    /**
     * Execute the submit results tool.
     * Returns a special marker that the tool loop can detect.
     */
    async execute(parameters) {
        const parseResult = safeParseToolParams(SubmitResultsParamsSchema, parameters);
        if (parseResult.success === false) {
            console.error('[SubmitResultsTool] Invalid parameters:', parseResult.error);
            // Still return a valid structure but with empty results
            return {
                accepted: true,
                message: `Results submitted with validation warnings: ${parseResult.error}`,
                [SUBMIT_RESULTS_MARKER]: true,
                submittedResults: {
                    results: [],
                    notes: `Validation error: ${parseResult.error}`,
                },
            };
        }
        const params = parseResult.data;
        console.log(`[SubmitResultsTool] Results submitted: ${params.results.length} items`);
        for (const result of params.results) {
            console.log(`  - Requirement ${result.requirementId}: ${result.rationale.slice(0, 80)}...`);
        }
        return {
            accepted: true,
            message: `Successfully submitted ${params.results.length} result(s)`,
            [SUBMIT_RESULTS_MARKER]: true,
            submittedResults: params,
        };
    }
}
/**
 * Check if a tool result is from submitResults
 */
export function isSubmitResultsResponse(result) {
    return (typeof result === 'object' &&
        result !== null &&
        SUBMIT_RESULTS_MARKER in result &&
        result[SUBMIT_RESULTS_MARKER] === true);
}
export default SubmitResultsTool;
